import { useEffect, useRef, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { Toaster, ToastBar, toast as toastApi } from 'react-hot-toast';
import { FiX } from 'react-icons/fi';
import { AuthProvider } from './context/AuthContext';
import { LoginDrawerProvider } from './components/auth/LoginDrawer';
import useAuth from './hooks/useAuth';
import GlobalReviewChecker from './components/client/GlobalReviewChecker';
import ComingSoonGate from './components/common/ComingSoonGate';
import useCanonical from './hooks/useCanonical';

// Hook-only mount point. Keeps the document's <link rel="canonical">
// aligned with the SPA route so Google doesn't treat every sub-route
// as a duplicate of the homepage canonical baked into index.html.
function CanonicalUpdater({ location }) {
  useCanonical(location);
  return null;
}

function ScrollToTop({ location }) {
  const current = useLocation();
  const { pathname, hash } = location ?? current;
  useEffect(() => {
    // Cu React Router <Link to="/foo#section">, browser-ul nu execută anchor
    // scroll-ul nativ (e SPA navigation, nu full reload). Tratăm noi cazul.
    //
    // Pe rute lazy-loaded (toate paginile non-Home), când navigăm cross-page
    // cu hash, chunk-ul țintă e încă în Suspense când effect-ul rulează —
    // getElementById returnează null și am cădea pe scroll-top, ratând
    // ținta. Polling scurt (10 încercări × 50ms = 500ms total) prinde
    // mount-ul după ce React a hidratat chunk-ul, fără dependențe externe.
    let timer;
    let cancelled = false;
    const tryScrollToHash = (attempt = 0) => {
      if (cancelled) return;
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (attempt < 10) {
        timer = setTimeout(() => tryScrollToHash(attempt + 1), 50);
      } else {
        // Hash invalid sau chunk care n-a montat în 500ms — măcar
        // ducem userul la top-ul paginii noi, ca să nu rămână la
        // scroll-ul rutei anterioare. behavior:'instant' e obligatoriu:
        // index.css setează html{scroll-behavior:smooth}, deci fără el
        // chiar și scrollTo "simplu" animează.
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    };

    if (hash) {
      tryScrollToHash();
    } else {
      // INSTANT, nu smooth: animația de smooth-scroll pornită la
      // schimbarea rutei era întreruptă de tranziția de pagină /
      // conținutul care se încarcă async (ContentPage etc.), lăsând
      // pagina nouă blocată la jumătatea drumului spre top — utilizatorul
      // deschidea paginile legale "de la mijloc". Reset instant = pagina
      // nouă pornește mereu de sus.
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pathname, hash]);
  return null;
}
import AppShell from './components/layout/AppShell';
import LoadingSpinner from './components/common/LoadingSpinner';
import CookieBanner from './components/common/CookieBanner';
import ErrorBoundary from './components/common/ErrorBoundary';
import SplashScreen from './components/common/SplashScreen';
import OfflineBanner from './components/common/OfflineBanner';
import MissingPhoneGate from './components/common/MissingPhoneGate';
import { ConsentProvider } from './context/ConsentContext';
import useGA4PageView from './hooks/useGA4PageView';
import useViewTransitionLocation from './hooks/useViewTransitionLocation';
import useEdgeSwipeBack from './hooks/useEdgeSwipeBack';
import useAndroidBackButton from './hooks/useAndroidBackButton';
import usePushNotifications from './hooks/usePushNotifications';
import PushPrimingSheet from './components/push/PushPrimingSheet';
import { isNativeApp, isClientNativeApp, isBusinessApp } from './utils/platform';
import { CLIENT_REJECT_PATH, businessPostAuthPath } from './utils/businessMode';
import useDeepLinks from './hooks/useDeepLinks';
import lazyChunk from './utils/lazyChunk';
const ClientAppRedirect = lazyChunk(() => import('./pages/ClientAppRedirect'));
// Conditional at MODULE level so Vite dead-code-eliminates the import in
// production — a bare lazy(import(...)) would still emit the chunk.
const CalendarHarness = import.meta.env.DEV
  ? lazyChunk(() => import('./pages/dev/CalendarHarness'))
  : null;

// Business build '/' entry: route by the authenticated role (owner/specialist →
// dashboard, admin → /admin, client → reject screen) instead of hard-sending
// every role to the salon dashboard.
function BusinessRoot() {
  const { user, loading } = useAuth();
  if (loading) return <RouteSuspenseFallback />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={businessPostAuthPath(user.role) || '/salon/dashboard'} replace />;
}

// HomePage stays eager (it's the entry route, so lazy-loading it just
// adds a network round-trip for no benefit).
import HomePage from './pages/client/HomePage';

// /search legacy alias — HomePage IS the marketplace surface now, so
// any old link / bookmark with the /search?city=... shape forwards
// to / with the query string preserved. `replace: true` keeps the
// back button sensible (it doesn't loop back to /search).
function SearchRedirect() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  return <Navigate to={qs ? `/?${qs}` : '/'} replace />;
}

// All other routes are lazy-loaded. This keeps the initial bundle small
// (~300 KiB instead of 1.3 MB) and speeds up First Contentful Paint.
// Each chunk is fetched only when the user navigates to that route.
const SalonDetailPage = lazyChunk(() => import('./pages/client/SalonDetailPage'));
const SalonPublicReviewsPage = lazyChunk(() => import('./pages/client/SalonPublicReviewsPage'));
const BookingPage = lazyChunk(() => import('./pages/client/BookingPage'));
const BookingConfirmationPage = lazyChunk(() => import('./pages/client/BookingConfirmationPage'));
const CheckoutPage = lazyChunk(() => import('./pages/client/CheckoutPage'));
const MyBookingsPage = lazyChunk(() => import('./pages/client/MyBookingsPage'));
const ProfilePage = lazyChunk(() => import('./pages/client/ProfilePage'));
const AboutPage = lazyChunk(() => import('./pages/client/AboutPage'));
const ContentPage = lazyChunk(() => import('./pages/client/ContentPage'));
const FAQPage = lazyChunk(() => import('./pages/client/FAQPage'));
const ContactPage = lazyChunk(() => import('./pages/client/ContactPage'));
const MapPage = lazyChunk(() => import('./pages/client/MapPage'));
const BlogIndexPage = lazyChunk(() => import('./pages/client/BlogIndexPage'));
const BlogPostPage = lazyChunk(() => import('./pages/client/BlogPostPage'));
const UnsubscribedPage = lazyChunk(() => import('./pages/client/UnsubscribedPage'));

// Landing Page
const LandingPage = lazyChunk(() => import('./pages/LandingPage'));
const ProLandingPage = lazyChunk(() => import('./pages/ProLandingPage'));
const ProPricingPage = lazyChunk(() => import('./pages/ProPricingPage'));
const ProContactPage = lazyChunk(() => import('./pages/ProContactPage'));

// Auth Pages
const LoginPage = lazyChunk(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazyChunk(() => import('./pages/auth/RegisterPage'));
const ProLoginPage = lazyChunk(() => import('./pages/auth/ProLoginPage'));
const ProRegisterPage = lazyChunk(() => import('./pages/auth/ProRegisterPage'));
const ForgotPasswordPage = lazyChunk(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyChunk(() => import('./pages/auth/ResetPasswordPage'));
const AcceptInvitePage = lazyChunk(() => import('./pages/auth/AcceptInvitePage'));
const VerifyEmailPage = lazyChunk(() => import('./pages/auth/VerifyEmailPage'));
const MagicLinkPage = lazyChunk(() => import('./pages/auth/MagicLinkPage'));

// Salon Pages
const SalonLayout = lazyChunk(() => import('./components/layout/SalonLayout'));
const AdminLayout = lazyChunk(() => import('./components/layout/AdminLayout'));
const SalonDashboard = lazyChunk(() => import('./pages/salon/SalonDashboard'));
const SalonAppointmentsPage = lazyChunk(() => import('./pages/salon/SalonAppointmentsPage'));
const SalonServicesPage = lazyChunk(() => import('./pages/salon/SalonServicesPage'));
const SalonTeamPage = lazyChunk(() => import('./pages/salon/SalonTeamPage'));
const SalonClientsPage = lazyChunk(() => import('./pages/salon/SalonClientsPage'));
const VetConsultationPage = lazyChunk(() => import('./pages/salon/VetConsultationPage'));
const SalonStockPage = lazyChunk(() => import('./pages/salon/SalonStockPage'));
const SalonRegistryPage = lazyChunk(() => import('./pages/salon/SalonRegistryPage'));
const SalonAgendaPage = lazyChunk(() => import('./pages/salon/SalonAgendaPage'));
const SalonRecallsPage = lazyChunk(() => import('./pages/salon/SalonRecallsPage'));
const SalonPatientFilePage = lazyChunk(() => import('./pages/salon/SalonPatientFilePage'));
const SalonPatientsPage = lazyChunk(() => import('./pages/salon/SalonPatientsPage'));
const SalonAuditPage = lazyChunk(() => import('./pages/salon/SalonAuditPage'));
const SalonReviewsPage = lazyChunk(() => import('./pages/salon/SalonReviewsPage'));
const SalonStatsPage = lazyChunk(() => import('./pages/salon/SalonStatsPage'));
const SalonSettingsPage = lazyChunk(() => import('./pages/salon/SalonSettingsPage'));
// Financiar hub — /salon/billing, /salon/payments și /salon/commissions
// montează toate același hub cu taburi; paginile-panou sunt lazy-loaded
// din interiorul hubului, nu de aici.
const SalonFinancePage = lazyChunk(() => import('./pages/salon/SalonFinancePage'));
const SalonNotificationsPage = lazyChunk(() => import('./pages/salon/SalonNotificationsPage'));
const SalonSpecialistProfilePage = lazyChunk(() => import('./pages/salon/SalonSpecialistProfilePage'));
const SalonWaitlistPage = lazyChunk(() => import('./pages/salon/SalonWaitlistPage'));
const SalonSupportPage = lazyChunk(() => import('./pages/salon/SalonSupportPage'));
const ContinueBookingPage = lazyChunk(() => import('./pages/client/ContinueBookingPage'));

// Admin Pages
const AdminDashboard = lazyChunk(() => import('./pages/admin/AdminDashboard'));
const AdminUsersPage = lazyChunk(() => import('./pages/admin/AdminUsersPage'));
const AdminSalonsPage = lazyChunk(() => import('./pages/admin/AdminSalonsPage'));
const AdminFinancePage = lazyChunk(() => import('./pages/admin/AdminFinancePage'));
const AdminPnLPage = lazyChunk(() => import('./pages/admin/AdminPnLPage'));
const AdminMessagingPage = lazyChunk(() => import('./pages/admin/AdminMessagingPage'));
const AdminReviewsPage = lazyChunk(() => import('./pages/admin/AdminReviewsPage'));
const AdminSubscriptionsPage = lazyChunk(() => import('./pages/admin/AdminSubscriptionsPage'));
const AdminMarketingPage = lazyChunk(() => import('./pages/admin/AdminMarketingPage'));
const AdminLogsPage = lazyChunk(() => import('./pages/admin/AdminLogsPage'));
const AdminSupportPage = lazyChunk(() => import('./pages/admin/AdminSupportPage'));
const AdminBookingsPage = lazyChunk(() => import('./pages/admin/AdminBookingsPage'));
const AdminContentPage = lazyChunk(() => import('./pages/admin/AdminContentPage'));
const AdminSalesPage = lazyChunk(() => import('./pages/admin/AdminSalesPage'));

// Null component: registers the device for APNs push while signed in. Lives
// inside AuthProvider (needs `user`) + the Router. See usePushNotifications.
function PushRegistrar() {
  usePushNotifications();
  return null;
}

// Protected Route - checks auth + role
function ProtectedRoute({ allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    // Capture where the user meant to go so LoginPage can bounce them
    // back after successful auth. pathname+search only — we don't want
    // the hash in post-auth redirects (it's transient UI state).
    const from = location.pathname + (location.search || '');
    return <Navigate to="/login" state={{ from }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // In the business build '/' redirects to the dashboard (owner/specialist),
    // so a wrong-role (CLIENT) user sent to '/' would loop; route them to the
    // reject screen instead. On web/client-native '/' is the marketplace.
    return <Navigate to={isBusinessApp ? CLIENT_REJECT_PATH : '/'} replace />;
  }

  return <Outlet />;
}


// Fallback shown while a lazy route chunk is downloading.
function RouteSuspenseFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner />
    </div>
  );
}

// Wraps BookingPage with a key tied to the URL path so an in-place
// navigation between two salon-booking URLs (manual URL edit, or any
// future "switch salon" flow) triggers a full re-mount. Without this,
// React reuses the same BookingPage instance and the wizard's local
// state (salon ref, selectedServices, selectedSpecialist, selectedDate
// etc.) leaks from the previous salon — service IDs scoped to salon A
// would not resolve against salon B's services, silently corrupting
// the booking. (The companion fix for localStorage draftId leak
// across salons lives in src/utils/bookingDraft.js.)
//
// Keying on pathname instead of useParams().slug because the latter
// silently fails if the route param is renamed (e.g. /:slug →
// /:salonSlug) — pathname is decoupled from the route definition.
function KeyedBookingPage() {
  const { pathname } = useLocation();
  return <BookingPage key={pathname} />;
}

// 404 Page
function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center">
        <div className="text-8xl font-black text-gray-200 mb-4">404</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Pagina nu a fost găsită</h1>
        <p className="text-gray-500 mb-6">Ne pare rău, pagina pe care o cauți nu există.</p>
        <a href="/" className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors">
          Înapoi la pagina principală
        </a>
      </div>
    </div>
  );
}

// Tiny adapter component so the GA4 page-view hook can run inside the
// Router context (it depends on useLocation). Calling the hook directly
// in App() works too, but keeping it as its own node makes the
// "analytics tracking happens here" intent explicit at the call-site.
function GA4PageViewTracker({ location }) {
  useGA4PageView(location);
  return null;
}

function ImpersonationBanner() {
  const impersonating = JSON.parse(localStorage.getItem('impersonating') || 'null');
  const bannerRef = useRef(null);
  // The banner is position:fixed (overlays the top) and its height VARIES — it
  // wraps to two lines on mobile. Publish the measured height as --imp-banner-h
  // so the salon layout's sticky menu header (and the drawer) can sit BELOW it
  // instead of hiding under it. Cleared to 0 when not impersonating, so nothing
  // shifts in the normal case.
  useEffect(() => {
    const root = document.documentElement;
    const el = bannerRef.current;
    if (!impersonating || !el) {
      root.style.setProperty('--imp-banner-h', '0px');
      return undefined;
    }
    const measure = () => root.style.setProperty('--imp-banner-h', `${el.offsetHeight}px`);
    // measure() runs unconditionally, so the var is set even where
    // ResizeObserver is unavailable (jsdom, if unit tests are ever added; the
    // observer only re-measures on later reflows like a mobile line-wrap).
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => { ro?.disconnect(); root.style.setProperty('--imp-banner-h', '0px'); };
  }, [impersonating?.email]);

  if (!impersonating) return null;

  const handleReturn = () => {
    const adminToken = localStorage.getItem('adminToken');
    localStorage.setItem('token', adminToken);
    localStorage.removeItem('adminToken');
    localStorage.removeItem('impersonating');
    window.location.href = '/admin';
  };

  return (
    <div ref={bannerRef} className="fixed top-0 left-0 right-0 z-[100] bg-red-600 text-white text-center pb-2 pt-[calc(env(safe-area-inset-top,0px)+0.5rem)] text-sm font-medium">
      Ești logat ca <strong>{impersonating.firstName} {impersonating.lastName}</strong> ({impersonating.email}) —{' '}
      <button onClick={handleReturn} className="underline font-bold hover:text-red-200">
        Revenire la admin
      </button>
    </div>
  );
}

export default function App() {
  // Trails the real location by one commit so route swaps can run
  // inside document.startViewTransition (cross-fade between pages).
  const displayedLocation = useViewTransitionLocation();
  // iOS edge-swipe-to-go-back in JS (the native WKWebView gesture is off — it
  // flashed a stale snapshot). Fires history.back() → the View Transition slide.
  useEdgeSwipeBack();
  // Android hardware/gesture back → same history.back(); exits the app at the
  // first screen instead of trapping the user in the WebView.
  useAndroidBackButton();
  // Native deep links (Universal Links / App Links) → in-app router navigation.
  // Dormant until the native projects declare associated domains.
  useDeepLinks();
  return (
    <>
    <SplashScreen />
    <OfflineBanner />
    <ImpersonationBanner />
    <ConsentProvider>
    <AuthProvider>
    <PushRegistrar />
    <PushPrimingSheet />
    <LoginDrawerProvider>
      {/* Toaster children render adds an explicit dismiss button to
          every toast. Loading toasts skip it (they unmount automatically
          on resolve / reject; an X there would let the user dismiss the
          spinner mid-flight and lose the eventual outcome). */}
      <Toaster
        position="top-right"
        // Push the toast stack below the iOS status bar / notch / Dynamic
        // Island in the Capacitor shell, where viewport-fit=cover lets content
        // run under it. env(safe-area-inset-top) is 0 on web/Android, so this
        // collapses to react-hot-toast's normal 16px top offset there.
        containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
        toastOptions={{ duration: 4000 }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                {t.type !== 'loading' && (
                  <button
                    type="button"
                    // stopPropagation so the dismiss click never reaches
                    // a toast-level handler. None exists today, but if a
                    // future toast gets a click-to-navigate body, the X
                    // must close the toast WITHOUT also firing that
                    // navigation.
                    onClick={(e) => { e.stopPropagation(); toastApi.dismiss(t.id); }}
                    aria-label="Închide notificarea"
                    className="ml-2 -mr-1 p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
      <MissingPhoneGate />
      {!isBusinessApp && <GlobalReviewChecker />}
      {/* Drive these route side-effects off the trailing displayedLocation
          so they run when the new page is actually shown, not one commit
          early on the old DOM — otherwise ScrollToTop resets scroll before
          startViewTransition snapshots the outgoing page and the cross-fade
          animates from the wrong position. */}
      <ScrollToTop location={displayedLocation} />
      <GA4PageViewTracker location={displayedLocation} />
      <CanonicalUpdater location={displayedLocation} />
      {/* Pre-launch curtain over the client marketplace. Inert unless the
          VITE_COMING_SOON=1 build flag is set (Vercel Production only) — see
          the component for the full gating story (native-safe, auth bypass,
          App-Review-safe route allowlist). */}
      <ComingSoonGate />
      <ErrorBoundary>
      <Suspense fallback={<RouteSuspenseFallback />}>
      <Routes location={displayedLocation}>
        {/* Landing + B2B marketing — WEB ONLY. Neither native binary ships the
            marketing pages (App Store 3.1.1 anti-steering + 4.2). The client
            app additionally redirects any /pro/* URL home; the business app
            does NOT (it serves /pro/login + /pro/register below), so its
            catch-all redirect is gated to the client-native build only. */}
        {!isNativeApp ? (
          <>
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/pro" element={<ProLandingPage />} />
            <Route path="/pro/preturi" element={<ProPricingPage />} />
            <Route path="/pro/contact" element={<ProContactPage />} />
          </>
        ) : isClientNativeApp ? (
          <Route path="/pro/*" element={<Navigate to="/" replace />} />
        ) : null}

        {/* Auth pages - no layout */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {/* Business build: a CLIENT account that signs in is sent here (see
            businessPostAuthPath) — the consumer-app landing. Harmless on web /
            client-native, so it's always registered. */}
        <Route path={CLIENT_REJECT_PATH} element={<ClientAppRedirect />} />
        {/* Salon SIGN-IN: web + business-native (not the client B2C binary). */}
        {!isClientNativeApp && (
          <Route path="/pro/login" element={<ProLoginPage />} />
        )}
        {/* Salon REGISTRATION is WEB-ONLY. The native business app is sign-in
            only: App Store 3.1.1 treats in-app registration for an
            externally-paid B2B subscription as an external-purchase mechanism
            (rejected 2026-07-16). New salons register on the web; the app just
            signs them in. */}
        {!isNativeApp && (
          <Route path="/pro/register" element={<ProRegisterPage />} />
        )}
        {/* Native BUSINESS app: registration lives on the web, so a stray in-app
            nav or deep link to /pro/register lands on the salon sign-in (the
            canonical business login, per BusinessRoot) — never a registration
            screen (defense-in-depth for 3.1.1) and no bare 404. The client-native
            binary already redirects /pro/* → / above, so it's excluded here. */}
        {isNativeApp && isBusinessApp && (
          <Route path="/pro/register" element={<Navigate to="/login" replace />} />
        )}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* Passwordless magic-link landing for sales-activated trial owners.
            PUBLIC by design — the owner has no session yet. WEB + BUSINESS
            builds (it's the owner entry point from WhatsApp alerts); excluded
            only from the client-native B2C binary. */}
        {!isClientNativeApp && <Route path="/admin/magic/:token" element={<MagicLinkPage />} />}

        {/* Salon dashboard + Admin — WEB + BUSINESS builds. Excluded only from
            the client-native B2C binary (App Store 3.1.1 + 4.2). /salon/:slug
            (the public client detail) stays — it lives in the AppShell group
            below, not here. */}
        {!isClientNativeApp && (<>
        {/* Salon dashboard - own layout with sidebar (must be before /salon/:slug) */}
        <Route element={<SalonLayout />}>
          <Route path="/salon/dashboard" element={<SalonDashboard />} />
          <Route path="/salon/appointments" element={<SalonAppointmentsPage />} />
          <Route path="/salon/notifications" element={<SalonNotificationsPage />} />
          <Route path="/salon/services" element={<SalonServicesPage />} />
          <Route path="/salon/team" element={<SalonTeamPage />} />
          <Route path="/salon/clienti" element={<SalonClientsPage />} />
          <Route path="/salon/consultatie" element={<VetConsultationPage />} />
          {/* A signed consultation is a legal document; it gets its own address
              so it can be reopened rather than seen once. */}
          <Route path="/salon/consultatie/:consultationId" element={<VetConsultationPage />} />
          <Route path="/salon/stoc" element={<SalonStockPage />} />
          <Route path="/salon/registre" element={<SalonRegistryPage />} />
          <Route path="/salon/agenda" element={<SalonAgendaPage />} />
          <Route path="/salon/rechemari" element={<SalonRecallsPage />} />
          <Route path="/salon/pacienti" element={<SalonPatientsPage />} />
          <Route path="/salon/audit" element={<SalonAuditPage />} />
          <Route path="/salon/pacient/:petId" element={<SalonPatientFilePage />} />
          <Route path="/salon/reviews" element={<SalonReviewsPage />} />
          <Route path="/salon/stats" element={<SalonStatsPage />} />
          <Route path="/salon/commissions" element={<SalonFinancePage />} />
          <Route path="/salon/profile" element={<SalonSpecialistProfilePage />} />
          <Route path="/salon/waitlist" element={<SalonWaitlistPage />} />
          <Route path="/salon/billing" element={<SalonFinancePage />} />
          <Route path="/salon/invoices" element={<SalonFinancePage />} />
          <Route path="/salon/payments" element={<SalonFinancePage />} />
          <Route path="/salon/support" element={<SalonSupportPage />} />
          <Route path="/salon/settings" element={<SalonSettingsPage />} />
        </Route>

        {/* Admin dashboard - own layout with sidebar */}
        <Route element={<AdminLayout />}>
          <Route element={<ProtectedRoute allowedRoles={['ADMIN']} />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/salons" element={<AdminSalonsPage />} />
            <Route path="/admin/sales" element={<AdminSalesPage />} />
            <Route path="/admin/bookings" element={<AdminBookingsPage />} />
            <Route path="/admin/finance" element={<AdminFinancePage />} />
            <Route path="/admin/pnl" element={<AdminPnLPage />} />
            <Route path="/admin/messaging" element={<AdminMessagingPage />} />
            <Route path="/admin/reviews" element={<AdminReviewsPage />} />
            <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="/admin/marketing" element={<AdminMarketingPage />} />
            <Route path="/admin/logs" element={<AdminLogsPage />} />
            <Route path="/admin/support" element={<AdminSupportPage />} />
            <Route path="/admin/content" element={<AdminContentPage />} />
          </Route>
        </Route>
        </>)}

        {/* All public + client-protected pages share AppShell —
            clean unified header + footer + outlet (with filter-modal
            state on context for marketplace surfaces that opt in). */}
        <Route element={<AppShell />}>
          {/* Business build has no consumer marketplace: '/' routes to the
              dashboard, and SalonLayout's gate handles the rest (→ /login when
              signed out, → reject screen for a CLIENT account). */}
          {/* DEV-only visual harness for the multi-specialist calendar CSS —
              tree-shaken out of production builds by the DEV guard. */}
          {import.meta.env.DEV && (
            <Route path="/dev/calendar-harness" element={<CalendarHarness />} />
          )}
          <Route path="/" element={isBusinessApp ? <BusinessRoot /> : <HomePage />} />
          <Route path="/search" element={<SearchRedirect />} />
          {/* The consumer map is the ONLY thing in the bundle that reaches
              CoreLocation (MapPage → getCurrentPositionAsync). A salon owner has
              no use for it, but while the route resolved, a deep link could raise
              an iOS location prompt in the business app — under a purpose string
              that correctly says we never ask for location. Gate the route and
              that contradiction can't happen. Same reasoning as /faq below: it's
              not enough that nothing links there, the route must not resolve.
              /search is fine — it redirects to /, which is BusinessRoot here. */}
          {!isBusinessApp && <Route path="/map" element={<MapPage />} />}
          {!isNativeApp && <Route path="/about" element={<AboutPage />} />}
          {/* FAQ serves backend content that includes the subscription price
              ("Petzy Pro costă 149 RON/lună…") — kept out of the business
              binary (Apple 3.1.1). Nothing links to it there, but the route
              itself must not resolve either. */}
          {!isBusinessApp && <Route path="/faq" element={<FAQPage />} />}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<ContentPage />} />
          <Route path="/privacy" element={<ContentPage />} />
          <Route path="/cookies" element={<ContentPage />} />
          <Route path="/gdpr" element={<ContentPage />} />
          <Route path="/cancel-policy" element={<ContentPage />} />
          {!isNativeApp && <Route path="/register-salon-info" element={<ContentPage />} />}
          {!isNativeApp && (
            <>
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
            </>
          )}
          <Route path="/unsubscribed" element={<UnsubscribedPage />} />
          <Route path="/salon/:slug" element={<SalonDetailPage />} />
          <Route path="/salon/:slug/reviews" element={<SalonPublicReviewsPage />} />

          {/* Bottom-nav tabs that need an account but stay reachable as guests:
              each renders a friendly log-in prompt INSIDE the shell (tab bar +
              header intact) instead of bouncing to /login — the Fresha pattern.
              The pages short-circuit to GuestPrompt when there's no user. */}
          <Route path="/my-bookings" element={<MyBookingsPage />} />
          <Route path="/profile" element={<ProfilePage />} />

          {/* Protected Client Routes — open to ANY authenticated role. The
              booking flow genuinely needs auth (profile / "my bookings" moved
              up — they handle guests themselves). A salon owner / specialist /
              admin is also a person who books grooming for their own pet; the
              data model keeps client (clientId) and worker (specialistId)
              bookings separate, so "my bookings" only ever shows what the user
              booked as a client. (Native is client-only anyway — no dashboard.) */}
          <Route element={<ProtectedRoute allowedRoles={['CLIENT', 'SPECIALIST', 'SALON_OWNER', 'ADMIN']} />}>
            <Route path="/booking" element={<KeyedBookingPage />} />
            <Route path="/salon/:slug/booking" element={<KeyedBookingPage />} />
            <Route path="/booking/confirmation" element={<BookingConfirmationPage />} />
            <Route path="/booking/payment" element={<CheckoutPage />} />
            <Route path="/booking/continue/:id" element={<ContinueBookingPage />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </LoginDrawerProvider>
    </AuthProvider>
    <CookieBanner />
    </ConsentProvider>
    </>
  );
}
